import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatDayRange } from "@/lib/equipment/availability";
import type { DraftLine, DraftWarning } from "@/lib/equipment/booking-draft";
import { TONE_TEXT } from "@/lib/widget-status";
import type { DBEquipmentItem } from "@/lib/db-types";

interface BookingLinesTableProps {
  lines: DraftLine[];
  items: Map<string, DBEquipmentItem>;
  warnings: Map<string, DraftWarning>;
  departmentName: (id: string | null) => string;
  readOnly: boolean;
  onChange: (key: string, patch: Partial<DraftLine>) => void;
  onRemove: (key: string) => void;
}

const th = "py-2 px-2 font-medium text-muted-foreground text-xs uppercase tracking-wider text-left";

/** Status under navnet: ledig, ledig antall for mengdevare og ⚠. */
function lineStatus(item: DBEquipmentItem | undefined, line: DraftLine, w: DraftWarning | undefined) {
  if (!item || !w) return null;
  const parts: { text: string; warn: boolean }[] = [];
  if (w.conflicts.length > 0) parts.push({ text: "⚠ også booket", warn: true });
  else if (item.quantity > 1)
    parts.push({ text: `vi eier ${item.quantity} · ${w.available} ledige ${formatDayRange(line.date_from, line.date_to)}`, warn: w.available < line.quantity });
  else parts.push({ text: "Ledig", warn: false });
  return parts;
}

/** Linjene i bookingdialogen med egne datoer, antall og notat (mockup F). */
export function BookingLinesTable({ lines, items, warnings, departmentName, readOnly, onChange, onRemove }: BookingLinesTableProps) {
  if (lines.length === 0) {
    return <p className="text-sm text-muted-foreground py-2">Ingen utstyr lagt til ennå.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className={th}>Utstyr</th>
            <th className={th}>Antall</th>
            <th className={th}>Datoer</th>
            <th className={th}>Notat</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const item = items.get(line.item_id);
            const status = lineStatus(item, line, warnings.get(line.key));
            const name = item?.name ?? "Ukjent utstyr";
            return (
              <tr key={line.key} className="border-b border-border/50 last:border-0 align-top">
                <td className="py-2 px-2 min-w-[12rem]">
                  <div className="font-medium">{name}</div>
                  <div className="text-xs text-muted-foreground">
                    {departmentName(item?.department_id ?? null)}
                    {status?.map((p) => (
                      <span key={p.text} className={p.warn ? TONE_TEXT.near : undefined}> · {p.text}</span>
                    ))}
                  </div>
                </td>
                <td className="py-2 px-2 w-20">
                  <Input
                    type="number"
                    min={1}
                    max={item?.quantity ?? 1}
                    value={line.quantity}
                    disabled={readOnly || (item?.quantity ?? 1) <= 1}
                    onChange={(e) => onChange(line.key, { quantity: Number(e.target.value) })}
                    aria-label={`Antall ${name}`}
                    className="h-8"
                  />
                </td>
                <td className="py-2 px-2">
                  <div className="flex items-center gap-1">
                    <Input
                      type="date"
                      value={line.date_from}
                      disabled={readOnly}
                      onChange={(e) =>
                        onChange(line.key, {
                          date_from: e.target.value,
                          ...(e.target.value > line.date_to ? { date_to: e.target.value } : {}),
                        })
                      }
                      aria-label={`Fra-dato ${name}`}
                      className="h-8 w-[9.5rem]"
                    />
                    <span className="text-muted-foreground">–</span>
                    <Input
                      type="date"
                      value={line.date_to}
                      min={line.date_from}
                      disabled={readOnly}
                      onChange={(e) => onChange(line.key, { date_to: e.target.value })}
                      aria-label={`Til-dato ${name}`}
                      className="h-8 w-[9.5rem]"
                    />
                  </div>
                </td>
                <td className="py-2 px-2 min-w-[10rem]">
                  <Input
                    value={line.note}
                    disabled={readOnly}
                    onChange={(e) => onChange(line.key, { note: e.target.value })}
                    placeholder="Hvem tar med …"
                    aria-label={`Notat ${name}`}
                    className="h-8"
                  />
                </td>
                <td className="py-2 px-1">
                  {!readOnly && (
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => onRemove(line.key)} aria-label={`Fjern ${name}`}>
                      <SrIcon name="x" className="h-4 w-4" />
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
